(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const lerp = (start, end, amount) => start + (end - start) * amount;

  function initHeader() {
    const header = document.querySelector('[data-agency-header]');
    if (!header) return;

    const toggle = header.querySelector('[data-agency-menu-toggle]');
    const mobileNav = header.querySelector('[data-agency-mobile-nav]');
    let open = header.classList.contains('is-open');

    const syncScrolled = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 18);
    };

    const closeMenu = () => {
      open = false;
      header.classList.remove('is-open');
      document.body.classList.remove('is-navigation-open');
      toggle?.setAttribute('aria-expanded', 'false');
    };

    if (toggle && toggle.dataset.bound !== 'true') {
      toggle.dataset.bound = 'true';
      toggle.addEventListener('click', () => {
        open = !open;
        header.classList.toggle('is-open', open);
        document.body.classList.toggle('is-navigation-open', open);
        toggle.setAttribute('aria-expanded', String(open));
      });
    }

    mobileNav?.querySelectorAll('a').forEach((link) => {
      if (link.dataset.bound === 'true') return;
      link.dataset.bound = 'true';
      link.addEventListener('click', closeMenu);
    });

    if (header.dataset.scrollBound !== 'true') {
      header.dataset.scrollBound = 'true';
      window.addEventListener('scroll', syncScrolled, { passive: true });
      window.addEventListener('resize', () => {
        if (window.innerWidth > 1100) closeMenu();
      });
    }

    syncScrolled();
  }

  function animateCounters(root = document) {
    const counters = root.querySelectorAll('[data-agency-counter]');
    if (!counters.length) return;

    const runCounter = (counter) => {
      if (counter.dataset.counted === 'true') return;
      counter.dataset.counted = 'true';

      const value = Number(counter.dataset.value || 0);
      const prefix = counter.dataset.prefix || '';
      const suffix = counter.dataset.suffix || '';

      if (reduceMotion) {
        counter.textContent = `${prefix}${value}${suffix}`;
        return;
      }

      const start = performance.now();
      const duration = 1400;

      const tick = (now) => {
        const progress = clamp((now - start) / duration, 0, 1);
        const eased = 1 - Math.pow(1 - progress, 4);
        counter.textContent = `${prefix}${Math.round(value * eased)}${suffix}`;

        if (progress < 1) requestAnimationFrame(tick);
      };

      requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          window.setTimeout(() => runCounter(entry.target), 120);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.35 });

    counters.forEach((counter) => observer.observe(counter));
  }

  function initHeroFlow(hero) {
    if (!hero || hero.dataset.heroFlowInitialized === 'true') return;
    hero.dataset.heroFlowInitialized = 'true';

    const interactionEnabled = hero.dataset.interactionEnabled === 'true' && !reduceMotion;
    const motionEnabled = hero.dataset.motionEnabled === 'true' && !reduceMotion;
    const strength = Number(hero.dataset.interactionStrength || 55) / 100;
    const cubes = Array.from(hero.querySelectorAll('[data-agency-cube]')).map((element, index) => ({
      element,
      index,
      x: Number(element.dataset.x || 50),
      y: Number(element.dataset.y || 50),
      isBottom: Number(element.dataset.y || 50) > 34,
    }));
    const state = { x: 62, y: 72, driftX: 0, driftY: 0, tiltX: 0, tiltY: 0, energy: 0, active: 0 };
    const target = { x: 62, y: 72, driftX: 0, driftY: 0, tiltX: 0, tiltY: 0, energy: 0, active: 0 };
    let rafId = 0;
    let visible = true;

    const applyState = () => {
      hero.style.setProperty('--hero-pointer-x', `${state.x}%`);
      hero.style.setProperty('--hero-pointer-y', `${state.y}%`);
      hero.style.setProperty('--hero-drift-x', state.driftX.toFixed(2));
      hero.style.setProperty('--hero-drift-y', state.driftY.toFixed(2));
      hero.style.setProperty('--hero-tilt-x', state.tiltX.toFixed(2));
      hero.style.setProperty('--hero-tilt-y', state.tiltY.toFixed(2));
      hero.style.setProperty('--hero-energy', state.energy.toFixed(3));
      hero.style.setProperty('--hero-pointer-active', state.active.toFixed(3));
    };

    const applyCubeState = (time = 0) => {
      cubes.forEach((cube) => {
        const dx = (state.x - cube.x) / 100;
        const dy = (state.y - cube.y) / 100;
        const distance = Math.hypot(dx * 1.12, dy * 1.35);
        const proximity = clamp(1 - distance * 3.15, 0, 1);
        
        // Sequential "Stair" wave: moves from left to right based on cube.x
        const stairWave = motionEnabled ? Math.sin(time * 0.0012 - cube.x * 0.045) : 0;
        const idleLift = motionEnabled ? Math.max(0, stairWave) * (cube.isBottom ? 12 : 5) : 0;
        
        const tier = cube.isBottom ? 1 : 0.48;
        const influence = proximity * state.active * strength * tier;
        
        const pushX = (cube.x - state.x) * influence * 0.34 + (idleLift * 0.2);
        const pushY = (cube.y - state.y) * influence * 0.22 - (idleLift * 0.1);
        const driftX = (state.x - 50) * (cube.isBottom ? 0.045 : 0.022) * strength;
        const driftY = (state.y - 50) * (cube.isBottom ? 0.032 : 0.018) * strength;
        const lift = -(influence * 30 + idleLift);
        const glow = clamp(influence * 1.18 + (idleLift * 0.04) + state.energy * (cube.isBottom ? 0.2 : 0.08), 0, 1);
        const scale = 1 + influence * 0.045;
        const tiltX = clamp((state.x - cube.x) * influence * 0.36, -9, 9);
        const tiltY = clamp((cube.y - state.y) * influence * 0.3, -8, 8);

        cube.element.style.setProperty('--cube-parallax-x', `${(driftX + pushX).toFixed(2)}px`);
        cube.element.style.setProperty('--cube-parallax-y', `${(driftY + pushY).toFixed(2)}px`);
        cube.element.style.setProperty('--cube-lift', `${lift.toFixed(2)}px`);
        cube.element.style.setProperty('--cube-glow', glow.toFixed(3));
        cube.element.style.setProperty('--cube-scale', scale.toFixed(3));
        cube.element.style.setProperty('--cube-tilt-x', tiltX.toFixed(2));
        cube.element.style.setProperty('--cube-tilt-y', tiltY.toFixed(2));
      });
    };

    let isPausedByHeader = false;
    const header = document.querySelector('[data-agency-header]');
    if (header) {
      header.addEventListener('mouseenter', () => { 
        isPausedByHeader = true; 
        document.documentElement.classList.add('is-header-hovered');
      });
      header.addEventListener('mouseleave', () => { 
        isPausedByHeader = false; 
        document.documentElement.classList.remove('is-header-hovered');
        startLoop(); 
      });
    }

    const step = (time = 0) => {
      if (isPausedByHeader && state.active === 0) {
        rafId = 0;
        return;
      }

      state.x = lerp(state.x, target.x, 0.08);
      state.y = lerp(state.y, target.y, 0.08);
      state.driftX = lerp(state.driftX, target.driftX, 0.07);
      state.driftY = lerp(state.driftY, target.driftY, 0.07);
      state.tiltX = lerp(state.tiltX, target.tiltX, 0.06);
      state.tiltY = lerp(state.tiltY, target.tiltY, 0.06);
      state.energy = lerp(state.energy, target.energy, 0.08);
      state.active = lerp(state.active, target.active, 0.08);

      applyState();
      applyCubeState(time);

      if (visible && (motionEnabled || interactionEnabled)) {
        rafId = requestAnimationFrame(step);
      } else {
        rafId = 0;
      }
    };

    const startLoop = () => {
      if (rafId || !(motionEnabled || interactionEnabled) || !visible) return;
      rafId = requestAnimationFrame(step);
    };

    const stopLoop = () => {
      if (!rafId) return;
      cancelAnimationFrame(rafId);
      rafId = 0;
    };

    const onPointerMove = (event) => {
      if (!interactionEnabled || isPausedByHeader) return;

      const rect = hero.getBoundingClientRect();
      const nx = clamp((event.clientX - rect.left) / rect.width, 0, 1);
      const ny = clamp((event.clientY - rect.top) / rect.height, 0, 1);

      target.x = nx * 100;
      target.y = ny * 100;
      target.driftX = (nx - 0.5) * 42 * strength;
      target.driftY = (ny - 0.5) * 34 * strength;
      target.tiltX = clamp((nx - 0.5) * 48 * strength, -24, 24);
      target.tiltY = clamp((ny - 0.5) * 38 * strength, -20, 20);
      target.energy = clamp(0.28 + (1 - Math.abs(ny - 0.72)) * 0.6, 0, 1);
      target.active = 1;

      startLoop();
    };

    const resetPointer = () => {
      target.x = 62;
      target.y = 72;
      target.driftX = 0;
      target.driftY = 0;
      target.tiltX = 0;
      target.tiltY = 0;
      target.energy = motionEnabled ? 0.32 : 0;
      target.active = 0;
      startLoop();
    };

    hero.addEventListener('pointermove', onPointerMove, { passive: true });
    hero.addEventListener('pointerdown', onPointerMove, { passive: true });
    hero.addEventListener('pointerleave', resetPointer, { passive: true });

    if ('IntersectionObserver' in window) {
      const visibilityObserver = new IntersectionObserver((entries) => {
        visible = entries[0]?.isIntersecting ?? true;

        if (visible) {
          startLoop();
        } else {
          stopLoop();
        }
      });
      visibilityObserver.observe(hero);
    }

    applyState();
    applyCubeState();
    resetPointer();

    if (!motionEnabled && !interactionEnabled) {
      stopLoop();
    }
  }

  function initHeroes(root = document) {
    root.querySelectorAll('[data-agency-hero]').forEach((hero) => initHeroFlow(hero));
  }

  /* ──────────────────────────────────────────────────────────
   *  SERVICES ATLAS — Scroll-driven card reveal (v2)
   *
   *  How it works:
   *  - Each chapter's cards are moved into a "viewport" div once
   *    during init and stay there permanently (no cloning/moving).
   *  - On desktop (>1100px), viewport becomes sticky. Cards are
   *    absolutely positioned. Scroll progress through the chapter
   *    stack controls which card is visible (sa-card-active class).
   *  - On mobile, viewport is a normal flowing div, cards stack
   *    vertically as regular flow elements.
   *  - A spacer div provides the scroll height needed for paging.
   *  - Each chapter is fully isolated with its own scroll math.
   * ────────────────────────────────────────────────────────── */
  function initServicesAtlas(root = document) {
    const atlas = root.querySelector('[data-services-atlas]');
    if (!atlas || atlas.dataset.servicesAtlasInitialized === 'true') return;
    atlas.dataset.servicesAtlasInitialized = 'true';

    // Premium focus-scroll effect for the cards
    const cards = atlas.querySelectorAll('.service-record');
    
    // Extremely tight focus zone:
    // -40% bottom: Card stays fully blurred until it reaches just below the absolute center.
    // -45% top: Card starts blurring again immediately as it leaves the absolute center.
    const observerOptions = {
      root: null,
      rootMargin: '-45% 0px -40% 0px',
      threshold: 0
    };

    const cardObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const card = entry.target;
        
        if (entry.isIntersecting) {
          // Card is in the focus zone
          card.classList.add('is-focused');
          card.classList.remove('is-passed');
        } else {
          card.classList.remove('is-focused');
          // If the element's bounding rect top is negative relative to the root bound,
          // it means it has scrolled up past the viewport center.
          if (entry.boundingClientRect.top < window.innerHeight / 2) {
            card.classList.add('is-passed');
          } else {
            card.classList.remove('is-passed');
          }
        }
      });
    }, observerOptions);

    cards.forEach((card) => {
      cardObserver.observe(card);
    });

    // Optional: Also animate the category sticky sidebar if we want
    const chapters = atlas.querySelectorAll('[data-services-chapter]');
    const chapterObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.querySelector('.services-atlas__category').classList.add('is-active-category');
        } else {
          entry.target.querySelector('.services-atlas__category').classList.remove('is-active-category');
        }
      });
    }, { rootMargin: '-40% 0px -40% 0px' });

    chapters.forEach(chap => chapterObserver.observe(chap));
  }

  function initSpatialProcess(root) {
    const processSection = root.querySelector('[data-spatial-process]');
    if (!processSection) return;

    const sticky = processSection.querySelector('.spatial-process__sticky');
    const wheel = processSection.querySelector('[data-wheel]');
    const cards = processSection.querySelectorAll('.spatial-card');
    const progressBar = processSection.querySelector('[data-progress-bar]');

    if (!wheel || !cards.length) return;

    // Extract colors to update ambient background
    const cardColors = Array.from(cards).map(card => {
      return card.style.getPropertyValue('--card-color').trim() || '#0096ff';
    });

    let isIntersecting = false;
    const observer = new IntersectionObserver((entries) => {
      isIntersecting = entries[0].isIntersecting;
    });
    observer.observe(processSection);

    function update3DScene() {
      if (isIntersecting) {
        const rect = processSection.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Scrollable distance is section height minus window height
        const scrollableDistance = rect.height - windowHeight;
        
        // How much of the section has scrolled past the top of viewport
        let scrolled = -rect.top;
        
        let progress = scrolled / scrollableDistance;
        progress = Math.max(0, Math.min(1, progress)); // Clamp 0 to 1

        // 4 cards = 3 movements of 90 degrees = 270 degrees total rotation.
        const totalRotation = -270;
        const currentAngle = progress * totalRotation;

        // Apply physical rotation to the entire 3D wheel
        wheel.style.setProperty('--wheel-angle', `${currentAngle}deg`);
        
        // Apply progress bar fill
        if (progressBar) {
          progressBar.style.setProperty('--progress', `${progress * 100}%`);
        }

        // Determine which card is currently closest to the front (0deg relative)
        let activeIndex = Math.round(currentAngle / -90);
        activeIndex = Math.max(0, Math.min(cards.length - 1, activeIndex));

        // Update ambient environment color
        sticky.style.setProperty('--active-color', cardColors[activeIndex]);

        // Smoothly fade out cards that are rotating away
        cards.forEach((card, index) => {
          const targetAngle = -index * 90;
          const diff = Math.abs(currentAngle - targetAngle);
          
          // If diff is 0, opacity is 1. If diff is 90, opacity is ~0.1
          let opacity = 1 - (diff / 90) * 0.9;
          opacity = Math.max(0.1, Math.min(1, opacity));
          
          card.style.setProperty('--card-opacity', opacity);

          if (index === activeIndex) {
            card.classList.add('is-active');
          } else {
            card.classList.remove('is-active');
          }
        });
      }
      requestAnimationFrame(update3DScene);
    }

    requestAnimationFrame(update3DScene);
  }

  function initExperience(root = document) {
    initHeader();
    animateCounters(root);
    initHeroes(root);
    initServicesAtlas(root);
    initSpatialProcess(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initExperience());
  } else {
    initExperience();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initExperience(event.target);
  });
})();
